import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { useInfiniteQuery } from "@tanstack/react-query";

import { formatDate } from "../lib/format";
import { useTRPC } from "../lib/trpc";
import { KycStatusBadge } from "./KycStatusBadge";
import { LoadMoreButton } from "./LoadMoreButton";
import { TableMessageRow } from "./TableMessageRow";

const COLUMNS = 5;

/**
 * Every identity-verification session, newest first, from `users.kycVerifications`: the same
 * projection the Identity card shows for the latest one, never the document details.
 */
export function UserKycTab({ endUserId }: { endUserId: string }) {
  const trpc = useTRPC();
  const query = useInfiniteQuery(
    trpc.users.kycVerifications.infiniteQueryOptions(
      { endUserId, limit: 25 },
      { getNextPageParam: (last) => last.nextCursor },
    ),
  );
  const rows = query.data?.pages.flatMap((page) => page.data) ?? [];

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Started</TableHead>
            <TableHead>Standing</TableHead>
            <TableHead>Decided</TableHead>
            <TableHead>Veriff said</TableHead>
            <TableHead>Policy</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {query.isLoading && <TableMessageRow colSpan={COLUMNS}>Loading…</TableMessageRow>}
          {query.error && (
            <TableMessageRow colSpan={COLUMNS} tone="error">
              {query.error.message}
            </TableMessageRow>
          )}
          {query.isSuccess && rows.length === 0 && (
            <TableMessageRow colSpan={COLUMNS}>
              No verifications. A reset forgets earlier sessions.
            </TableMessageRow>
          )}
          {rows.map((row) => (
            <TableRow key={row.id}>
              <TableCell className="text-muted-foreground">{formatDate(row.createdAt)}</TableCell>
              <TableCell>
                <KycStatusBadge
                  status={row.status}
                  verified={row.status === "approved" && row.policyIssue === null}
                  policyIssue={row.policyIssue}
                />
              </TableCell>
              <TableCell className="text-muted-foreground">
                {row.decidedAt ? formatDate(row.decidedAt) : "—"}
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {row.providerCode || row.providerReason
                  ? [row.providerCode, row.providerReason].filter(Boolean).join(" · ")
                  : "—"}
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {row.isDuplicate ? "duplicate identity" : row.policyIssue ? row.policyIssue : "—"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <LoadMoreButton
        hasNextPage={query.hasNextPage}
        isFetchingNextPage={query.isFetchingNextPage}
        onClick={() => void query.fetchNextPage()}
      />
    </>
  );
}
